import * as React from 'react';
import { useForm, useField } from 'react-final-form';
import { ToggleFieldGroup, TogglePillField } from '@gandi/form/FinalForm';
import { useTranslator } from '@gandi/translate';
import { Base, Block, BlockContent, Label, Help } from '@gandi/ui';
import { useReadOnly } from '../../../formUtils/readOnly';
import { useFormInitialValues } from '../../../formUtils/useFormInitialValues';
import { useIsValidSection } from '../../../formUtils/useIsValidSection';
import CodeEditorField from './CodeEditorField';
import SectionActions from './SectionActions';
import SectionHeader from './SectionHeader';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const NAMESERVER_LIVEDNS = 'livedns';
const NAMESERVER_EXT = 'custom';
const DNSRECORDSTYPE_DEFAULT = 'default';
const DNSRECORDSTYPE_CUSTOM = 'custom';
const DNSRECORDSTYPE_MIGRATION = 'migration';
const defaultValue = {
  nameserversType: NAMESERVER_LIVEDNS,
  recordsType: DNSRECORDSTYPE_DEFAULT
};
const sectionName = 'nsAndRecords';
const NameserversSection = ({
  onDelete,
  disabled,
  ...blockProps
}) => {
  const {
    __
  } = useTranslator();
  const initialValues = useFormInitialValues();
  const {
    readOnly,
    setReadOnly,
    toggleReadOnly
  } = useReadOnly(sectionName);
  const {
    change,
    mutators
  } = useForm();
  const nameserversTypeName = `${sectionName}.nameserversType`;
  const nameserversName = `${sectionName}.nameservers`;
  const recordsName = `${sectionName}.records`;
  const recordsTypeName = `${sectionName}.recordsType`;
  const {
    input: {
      value: nsAndRecords
    }
    // $FlowFixMe[prop-missing]
  } = useField(sectionName, {
    subscription: {
      value: true
    }
  });
  React.useEffect(() => {
    if (!nsAndRecords) {
      change(sectionName, defaultValue);
    }
    if (!nsAndRecords) {
      // Not readonly on section creation
      // In case values change, this readonly state must be handled by the
      // controller to prevent unexpected results, e.g. readOnly false without
      // the section
      setReadOnly(sectionName, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isValidSection = useIsValidSection(sectionName);
  const nameserversOptions = [{
    label: __('Gandi LiveDNS'),
    value: NAMESERVER_LIVEDNS
  }, {
    label: __('External'),
    value: NAMESERVER_EXT
  }];
  const dnsRecordsTypeOptions = [{
    label: __('Default records'),
    value: 'default'
  }, {
    label: __('Custom'),
    value: 'custom'
  }];
  const handleDelete = () => {
    // ---
    // XXX: Field's submitErrors aren't cleared after removal which is probably a bug from final-form.
    mutators.clearSubmitError(sectionName);
    // ----

    change(sectionName, undefined);

    // change(sectionName, null);
    setReadOnly(sectionName, true);
    onDelete();
  };
  const handleCancel = () => {
    change(sectionName, initialValues?.nsAndRecords);
    setReadOnly(sectionName, true);
    if (!initialValues?.nsAndRecords) {
      onDelete();
    }
  };
  const handleDone = () => {
    // TODO: validate section with dry-run?
    setReadOnly(sectionName, true);
  };
  let recordsTypeHelp;
  switch (nsAndRecords.recordsType) {
    case undefined:
      recordsTypeHelp = __('DNS records are a set of information that connects your domain name to the server where you host your content. Providing correct DNS records are a necessary step for a functional website.');
      break;
    case DNSRECORDSTYPE_MIGRATION:
      recordsTypeHelp = __('Switching to our LiveDNS will not change your existing DNS records.');
      break;
    case DNSRECORDSTYPE_DEFAULT:
      recordsTypeHelp = __('By using Default DNS records, your domain contains all the necessary records for your domain to be live and is automatically configured to use GandiMail.');
      break;
    default:
    // nothing to ddo
  }
  return /*#__PURE__*/_jsxs(Block, {
    ...blockProps,
    children: [/*#__PURE__*/_jsx(SectionHeader, {
      title: __('Nameservers & DNS records'),
      onEditToggle: () => toggleReadOnly(sectionName),
      readOnlyMode: readOnly,
      disabled: disabled
    }), /*#__PURE__*/_jsxs(BlockContent, {
      children: [/*#__PURE__*/_jsx(ToggleFieldGroup, {
        name: nameserversTypeName,
        required: true,
        label: __('Nameservers'),
        help: nsAndRecords?.nameserversType === NAMESERVER_LIVEDNS ? __("By using Gandi's LiveDNS, our system can automatically update your DNS records.") : undefined,
        children: nameserversOptions.map(({
          label,
          value
        }) => /*#__PURE__*/_jsx(TogglePillField, {
          name: nameserversTypeName,
          value: value,
          readOnly: readOnly,
          children: label
        }, value))
      }), nsAndRecords?.nameserversType === NAMESERVER_EXT && /*#__PURE__*/_jsx(Base, {
        marginTop: "small",
        children: /*#__PURE__*/_jsx(CodeEditorField, {
          readOnly: readOnly,
          name: nameserversName,
          contentAttributes: {
            'aria-label': __('External nameservers')
          }
        })
      }), /*#__PURE__*/_jsxs(Base, {
        marginTop: "small",
        children: [nsAndRecords?.nameserversType === NAMESERVER_EXT && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Label, {
            target: "none",
            children: __('DNS Records')
          }), /*#__PURE__*/_jsx(Help, {
            children: __("DNS records can only be configured if you use Gandi's LiveDNS nameservers.")
          })]
        }), nsAndRecords?.nameserversType !== NAMESERVER_EXT && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ToggleFieldGroup, {
            name: recordsTypeName,
            readOnly: readOnly,
            required: true,
            help: recordsTypeHelp,
            children: dnsRecordsTypeOptions.map(({
              label,
              value
            }) => /*#__PURE__*/_jsx(TogglePillField, {
              name: recordsTypeName,
              value: value,
              readOnly: readOnly,
              children: label
            }, value))
          }), nsAndRecords?.recordsType === DNSRECORDSTYPE_CUSTOM && /*#__PURE__*/_jsx(Base, {
            marginTop: "small",
            children: /*#__PURE__*/_jsx(CodeEditorField, {
              readOnly: readOnly,
              name: recordsName,
              contentAttributes: {
                'aria-label': __('Custom DNS records')
              },
              type: "zonefile"
            })
          })]
        })]
      })]
    }), !readOnly && /*#__PURE__*/_jsx(SectionActions, {
      isValidSection: isValidSection,
      onDelete: handleDelete,
      onCancel: handleCancel,
      onDone: handleDone
    })]
  });
};
export default NameserversSection;